/**
 * upload/styles.ts — styles for the upload overlay and its three stages.
 *
 * EXTENDS, DOES NOT REPLACE. `shared.tsx`'s TOOLS_STYLES already defines the form language for these
 * pages (`.tb-field`, `.tb-note`, `.tb-chips`, inputs, buttons, tables) and the overlay renders INSIDE
 * a `.tb-tools` container, so all of that applies unchanged. Everything here is additive: the modal
 * shell, the stage stepper, the component-tree indentation and the completion indicators — the pieces
 * that genuinely did not exist before (there is no modal pattern anywhere in packages/web).
 *
 * Every colour is an existing `--tb-*` token from App.tsx (`--tb-ink`, `--tb-muted`, `--tb-line`,
 * `--tb-bg`, `--tb-surface`, `--tb-accent`, `--tb-accent-ink`, `--tb-banner-*`). No new palette, no
 * new library. Two literals appear — the green already used by `.tb-note--ok` (#2e7d32) for "done",
 * and a red (#b3261e) for "still needed" — and both are paired with a text label or an icon so
 * nothing is signalled by colour alone (PC-NFR01: no meaning by colour alone).
 *
 * Accessibility notes that are load-bearing rather than decorative:
 *   - `:focus-visible` outlines are never removed, and the overlay traps focus (see UploadOverlay).
 *   - The invalid state is a border colour AND a text message AND aria-invalid — three channels.
 *   - `prefers-reduced-motion` removes the only transition.
 */
export const UPLOAD_STYLES = `
  /* ── The overlay shell ─────────────────────────────────────────────────────────────────────── */
  .tb-ov-backdrop {
    position: fixed; inset: 0; z-index: 50;
    background: rgba(26, 28, 35, 0.55);
    display: flex; align-items: flex-start; justify-content: center;
    padding: 1.5rem 1rem; overflow-y: auto;
  }
  .tb-ov {
    background: var(--tb-bg); color: var(--tb-ink);
    border: 1px solid var(--tb-line); border-radius: 8px;
    width: 100%; max-width: 52rem;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
    display: flex; flex-direction: column;
    /* Cap the height so the sticky footer stays reachable on a long Instructions list. */
    max-height: calc(100vh - 3rem);
  }
  .tb-ov-head {
    display: flex; align-items: flex-start; gap: 1rem;
    padding: 1rem 1.25rem 0.75rem;
    border-bottom: 1px solid var(--tb-line);
  }
  .tb-ov-head h2 { margin: 0 0 0.15rem; font-size: 1.15rem; }
  .tb-ov-head .tb-ov-sub { margin: 0; color: var(--tb-muted); font-size: 0.9rem; }
  .tb-ov-close {
    margin-left: auto; flex: none;
    background: none; border: 1px solid var(--tb-line); color: var(--tb-ink);
    border-radius: 4px; font-size: 1rem; line-height: 1; padding: 0.35rem 0.55rem; cursor: pointer;
  }
  .tb-ov-close:hover { background: var(--tb-surface); }
  .tb-ov-body { padding: 1rem 1.25rem; overflow-y: auto; flex: 1 1 auto; }
  .tb-ov-foot {
    display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap;
    padding: 0.85rem 1.25rem; border-top: 1px solid var(--tb-line);
    background: var(--tb-surface); border-radius: 0 0 8px 8px;
  }
  .tb-ov-foot .tb-ov-spacer { margin-left: auto; }
  /* Inside the overlay the form should use the full width, overriding TOOLS_STYLES' 40rem cap. */
  .tb-ov form, .tb-ov .tb-ov-body form { max-width: none; }

  /* ── Stage stepper: Details -> Instructions -> Review ──────────────────────────────────────── */
  .tb-steps { display: flex; flex-wrap: wrap; gap: 0.4rem; list-style: none; margin: 0 0 1rem; padding: 0; }
  .tb-steps li { display: flex; align-items: center; gap: 0.4rem; font-size: 0.9rem; color: var(--tb-muted); }
  .tb-steps li + li::before { content: "→"; color: var(--tb-line); margin-right: 0.2rem; }
  .tb-steps .tb-step-n {
    display: inline-flex; align-items: center; justify-content: center;
    width: 1.4rem; height: 1.4rem; border-radius: 50%;
    border: 1px solid var(--tb-line); font-size: 0.78rem; font-weight: 600;
  }
  .tb-steps li[aria-current="step"] { color: var(--tb-ink); font-weight: 600; }
  .tb-steps li[aria-current="step"] .tb-step-n {
    background: var(--tb-accent); color: var(--tb-accent-ink); border-color: var(--tb-accent);
  }
  .tb-steps li[data-done="true"] .tb-step-n { background: #2e7d32; color: #fff; border-color: #2e7d32; }

  /* ── Progress counter ("7 of 10 steps explained") ──────────────────────────────────────────── */
  .tb-progress { display: flex; align-items: center; gap: 0.6rem; margin: 0 0 0.9rem; flex-wrap: wrap; }
  .tb-progress-bar {
    flex: 1 1 8rem; min-width: 6rem; height: 0.5rem;
    background: var(--tb-surface); border: 1px solid var(--tb-line); border-radius: 999px; overflow: hidden;
  }
  .tb-progress-bar > span { display: block; height: 100%; background: #2e7d32; transition: width 160ms ease; }
  .tb-progress-text { font-size: 0.9rem; font-weight: 600; }
  .tb-progress-text[data-complete="false"] { color: var(--tb-muted); }

  /* ── The component list ────────────────────────────────────────────────────────────────────── */
  .tb-comp-list { list-style: none; margin: 0; padding: 0; }
  .tb-comp {
    border: 1px solid var(--tb-line); border-radius: 6px;
    margin: 0 0 0.6rem; background: var(--tb-bg);
  }
  /* Nesting is shown by indentation AND by the branch label on the header, never by indent alone. */
  .tb-comp[data-depth="1"] { margin-left: 1.25rem; }
  .tb-comp[data-depth="2"] { margin-left: 2.5rem; }
  .tb-comp[data-depth="3"] { margin-left: 3.75rem; }
  .tb-comp[data-depth="4"] { margin-left: 5rem; }
  /* Beyond depth 4 the indent stops growing so a deep flow never scrolls sideways off the modal. */
  .tb-comp[data-depth="5"], .tb-comp[data-deep="true"] { margin-left: 6rem; }
  .tb-comp[data-missing="true"] { border-left: 4px solid #b3261e; }
  .tb-comp[data-missing="false"] { border-left: 4px solid #2e7d32; }

  .tb-comp-head {
    display: flex; align-items: baseline; gap: 0.5rem; flex-wrap: wrap;
    width: 100%; text-align: left;
    background: var(--tb-surface); border: none; border-radius: 5px 5px 0 0;
    padding: 0.55rem 0.7rem; cursor: pointer; color: var(--tb-ink); font: inherit;
  }
  .tb-comp-head:hover { background: var(--tb-line); }
  .tb-comp-num { font-weight: 700; font-variant-numeric: tabular-nums; }
  .tb-comp-label { font-weight: 600; }
  .tb-comp-branch {
    font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.03em;
    color: var(--tb-muted); border: 1px solid var(--tb-line);
    border-radius: 999px; padding: 0.05rem 0.45rem; background: var(--tb-bg);
  }
  .tb-comp-meta { display: block; width: 100%; color: var(--tb-muted); font-size: 0.82rem; font-weight: 400; }
  /* The state marker carries a glyph AND text, so it survives greyscale and screen readers. */
  .tb-comp-state { margin-left: auto; font-size: 0.82rem; font-weight: 600; white-space: nowrap; }
  .tb-comp-state[data-missing="true"] { color: #b3261e; }
  .tb-comp-state[data-missing="false"] { color: #2e7d32; }
  .tb-comp-body { padding: 0.6rem 0.7rem 0.75rem; }
  .tb-comp-body textarea { min-height: 5rem; }
  .tb-comp-body textarea[aria-invalid="true"] { border-color: #b3261e; }

  /* ── Inline field errors (Details + Instructions) ──────────────────────────────────────────── */
  .tb-err { display: block; color: #b3261e; font-size: 0.85rem; font-weight: 600; margin-top: 0.2rem; }
  .tb-tools .tb-ov input[aria-invalid="true"],
  .tb-tools .tb-ov textarea[aria-invalid="true"],
  .tb-tools .tb-ov select[aria-invalid="true"] { border-color: #b3261e; }

  /* ── Review summary ────────────────────────────────────────────────────────────────────────── */
  .tb-review-sec { border: 1px solid var(--tb-line); border-radius: 6px; padding: 0.75rem 0.9rem; margin: 0 0 1rem; }
  .tb-review-sec > h3 { margin: 0 0 0.5rem; font-size: 1rem; display: flex; align-items: center; gap: 0.75rem; }
  .tb-review-sec > h3 button { margin-left: auto; }
  .tb-review-step { border-left: 2px solid var(--tb-line); padding: 0 0 0 0.7rem; margin: 0 0 0.7rem; }
  .tb-review-step p { margin: 0.2rem 0 0; white-space: pre-wrap; }
  .tb-review-step .tb-comp-meta { margin-top: 0.1rem; }

  /* Duplicate preview cards reuse the note surface rather than introducing a card component. */
  .tb-dup { border: 1px solid var(--tb-line); border-radius: 6px; padding: 0.6rem 0.75rem; margin: 0 0 0.6rem; }
  .tb-dup h4 { margin: 0 0 0.2rem; font-size: 0.98rem; }
  .tb-dup p { margin: 0.1rem 0; }

  /* == Tag suggestions (CAT-F12) ============================================================== */
  /* ABSOLUTELY POSITIONED, deliberately. The panel opens directly above the sticky footer, so a
     panel that took up flow space would push the Next button down by up to twelve rems WHILE the
     builder is typing toward it. Overlaying the field hint instead costs nothing — the hint says
     what tags are for, which is advice they have stopped needing the moment they start typing. */
  .tb-suggest-wrap { position: relative; display: block; }
  .tb-suggest {
    position: absolute; left: 0; right: 0; top: calc(100% + 2px); z-index: 5;
    list-style: none; margin: 0; padding: 0.2rem 0;
    max-height: 12rem; overflow-y: auto;
    background: var(--tb-bg); color: var(--tb-ink);
    border: 1px solid var(--tb-line); border-radius: 4px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  }
  .tb-suggest li { margin: 0; }
  .tb-suggest button {
    display: flex; align-items: center; gap: 0.75rem; width: 100%;
    background: none; border: 0; color: inherit; font: inherit; font-weight: 400; text-align: left;
    padding: 0.3rem 0.6rem; cursor: pointer;
  }
  /* Hover and the keyboard's active row share ONE highlight, so arrowing down and pointing at a row
     look the same. The keyboard row is matched on aria-selected rather than a second class name —
     the attribute has to be right for a screen reader anyway, so styling it keeps the two honest. */
  .tb-suggest button:hover,
  .tb-suggest li[aria-selected="true"] > button { background: var(--tb-surface); }
  .tb-suggest .tb-suggest-label {
    flex: 1 1 auto; min-width: 0;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .tb-suggest .tb-suggest-meta { flex: none; color: var(--tb-muted); font-size: 0.85rem; }

  .tb-ov :focus-visible { outline: 2px solid var(--tb-accent); outline-offset: 2px; }

  @media (prefers-reduced-motion: reduce) {
    .tb-progress-bar > span { transition: none; }
  }
`;
